// The saved list page: the builder, the column picker, the sort headers and the
// results region, wired together.
//
// The one module on this page that looks itself up and is called from main.js.
// The three it drives take their elements as arguments, so each of them stays
// about one thing and this file owns the question of when to refetch.

import http from '../../shared/http.js';
import { initLiveList } from '../../shared/liveList.js';
import { initSortHeaders } from '../../shared/sortHeaders.js';
import { leseGruppe, serialisiere, parse } from '../../shared/filterTree.js';
import { initFilterBauer } from './filterBauer.js';
import { initSpaltenPicker } from './spaltenPicker.js';
import { hideLoading, notifyError, notifySuccess, showLoading } from '../../shared/notify.js';

// Long enough that typing a value is one request rather than fifteen, short
// enough that the row count still feels like it is keeping up. The same figure
// liveList.js uses for its search box.
const AENDERUNG_DEBOUNCE_MS = 350;

export function initListeErgebnis() {
  const region = document.getElementById('liste-ergebnis');
  if (!region) return;

  const listeId = region.dataset.liste;
  const baum = document.getElementById('filter-baum');
  const picker = document.getElementById('spalten-picker');
  const gruppierFeld = document.getElementById('gruppier-feld');
  const alleAnzeigen = document.getElementById('alle-anzeigen');

  // The sort in force, held here rather than read back out of the table: a
  // header click has to know the current sort to decide whether it is
  // reversing, and the fragment that arrives next replaces the header row.
  const url = new window.URLSearchParams(window.location.search);
  let sort = url.get('sort') || '';

  const bauer = initFilterBauer({
    wurzel: baum,
    onAenderung: () => planeAktualisierung()
  });

  const spaltenPicker = initSpaltenPicker({
    wurzel: picker,
    onAenderung: () => planeAktualisierung()
  });

  // Everything the results URL carries beyond paging: the tree as one encoded
  // parameter, the columns as a readable list, the sort and the grouping.
  //
  // Empties are dropped by liveList.js, so an emptied tree leaves the address
  // bar rather than sitting in it as filter={}.
  function extraParams() {
    return {
      filter: baum ? serialisiere(leseGruppe(baum)) : '',
      spalten: spaltenPicker ? spaltenPicker.spalten().join(',') : '',
      sort,
      gruppe: gruppierFeld ? gruppierFeld.value : '',
      // Empty rather than '0' when unchecked, so liveList drops it from the URL
      // instead of carrying a parameter that means "the default".
      alle: alleAnzeigen && alleAnzeigen.checked ? '1' : ''
    };
  }

  const liste = initLiveList({
    results: 'liste-ergebnis',
    path: `/listen/${listeId}`,
    extraParams
  });

  let timer = null;
  function planeAktualisierung() {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => liste && liste.refresh(), AENDERUNG_DEBOUNCE_MS);
  }

  // Sorting is immediate rather than debounced: it is a deliberate single
  // action, not something somebody is in the middle of.
  initSortHeaders({
    results: 'liste-ergebnis',
    current: () => sort,
    onSort: (neu) => {
      sort = neu;
      if (liste) liste.refresh();
    }
  });

  if (gruppierFeld) {
    gruppierFeld.addEventListener('change', () => liste && liste.refresh());
  }

  // Immediate rather than debounced, like the grouping select: a deliberate
  // single action, not something somebody is in the middle of.
  if (alleAnzeigen) {
    alleAnzeigen.addEventListener('change', () => liste && liste.refresh());
  }

  // The saved definition, rendered into the page by the server. This is what
  // "Zurücksetzen" restores, and it is why the builder needs no history of its
  // own - see the note on replaceState below.
  const gespeichert = baum ? parse(baum.dataset.definition) : null;
  if (bauer && gespeichert) bauer.aufbauen(gespeichert);

  const zuruecksetzen = document.getElementById('filter-zuruecksetzen');
  if (zuruecksetzen && bauer) {
    zuruecksetzen.addEventListener('click', async () => {
      await bauer.aufbauen(gespeichert);
      if (liste) liste.refresh();
    });
  }

  // Saving writes the tree, the columns, the sort and the grouping back onto
  // the list - which is what makes the next visit open on what is showing now.
  const speichern = document.getElementById('filter-speichern');
  if (speichern) {
    speichern.addEventListener('click', async () => {
      const nutzlast = {
        filter: baum ? leseGruppe(baum) : null,
        gruppierFeld: gruppierFeld && gruppierFeld.value ? gruppierFeld.value : null
      };

      if (spaltenPicker) nutzlast.spalten = spaltenPicker.spalten();

      if (sort) {
        nutzlast.sortFeld = sort.replace(/^-/, '');
        nutzlast.sortRichtung = sort.startsWith('-') ? 'desc' : 'asc';
      }

      speichern.disabled = true;
      showLoading('Liste wird gespeichert …');

      try {
        await http.patch(`/listenansichten/${listeId}`, nutzlast);
        hideLoading();
        notifySuccess('Liste gespeichert.');
        // The saved definition is now what is on screen, so a later reset
        // returns here rather than to what the page loaded with.
        if (baum) baum.dataset.definition = JSON.stringify(nutzlast.filter);

        // Refetch rather than trust the screen. A rule the builder considered
        // incomplete was never serialized into the results but *is* part of what
        // just got saved, and the server drops anything it cannot compile - so
        // the rows on screen can differ from the rows this list now means.
        // Pulling them again is what makes the save and the table agree.
        if (liste) await liste.refresh();
      } catch (err) {
        hideLoading();
        notifyError(err.response?.data?.message || 'Die Liste konnte nicht gespeichert werden.');
      } finally {
        speichern.disabled = false;
      }
    });
  }
}
